import { apiClient } from "@/services/api/axiosClient";
import type { ApiResponse } from "@/services/api/api.types";
import { API_ENDPOINTS } from "@/services/api/endpoints";
import type {
  AddPropertyPayload,
  DeletePropertyApiRequest,
  GetPropertiesApiResponse,
  GetPropertyDetailsFromAddressApiPayload,
  PriceDescriptionsResponse,
  PropertyApiResponse,
  PropertyDetailsFromAddress,
  PropertyDetailsFromAddressApiResponse,
  PropertyProtestApplicabilityResponse,
  PropertyProtestStatusResponse,
  PropertyRealStateAutoCompleteApiPayload,
  PropertyRealStateAutoCompleteApiResponse,
  PropertyRealStateAutoCompleteItemInfo,
  PropertyRealStateDetailApiPayload,
  PropertyRealStateDetailApiResponse,
  PropertyRealStateDetailInfo,
  SexOffendersOrCrimeIncidentsPayload,
  SexOffendersResponse,
  CrimeIncidentsResponse,
  ServiceExpansionRequestPayload,
  ServiceExpansionRequestResponse,
  UpdatePropertyDetailsPayload,
  UpdatePropertyApiRequest,
  UpdatePropertyApiResponse,
} from "./propertiesTypes";

export const propertyService = {
  async add(payload: AddPropertyPayload): Promise<ApiResponse<unknown>> {
    const { data } = await apiClient.post<Promise<ApiResponse<unknown>>>(
      API_ENDPOINTS.PROPERTY.ADD,
      payload
    );
    return data;
  },

  async update(payload: UpdatePropertyApiRequest): Promise<UpdatePropertyApiResponse> {
    console.log("update property by body payload: ", payload);
    const { data } = await apiClient.put<UpdatePropertyApiResponse>(
      API_ENDPOINTS.PROPERTY.UPDATE,
      payload
    );
    return data;
  },

  async getAll(queryParam: string): Promise<PropertyApiResponse[]> {
    const { data } = await apiClient.get<GetPropertiesApiResponse>(
      API_ENDPOINTS.PROPERTY.LIST(queryParam)
    );
    return data.data;
  },
  async getPriceDescriptions(): Promise<PriceDescriptionsResponse> {
    const { data } = await apiClient.get<ApiResponse<PriceDescriptionsResponse>>(
      API_ENDPOINTS.PROPERTY.PRICE_DESCRIPTIONS
    );
    return Array.isArray(data.data) ? data.data : [];
  },

  async getPropertyById(propertyId: string): Promise<PropertyApiResponse> {
    const { data } = await apiClient.get<ApiResponse<PropertyApiResponse>>(
      API_ENDPOINTS.PROPERTY.DETAILS(propertyId)
    );
    return data.data;
  },

  async updateDetails(
    propertyId: string,
    payload: UpdatePropertyDetailsPayload
  ): Promise<ApiResponse<unknown>> {
    const { data } = await apiClient.put<ApiResponse<unknown>>(
      API_ENDPOINTS.PROPERTY.DETAILS(propertyId),
      payload
    );
    return data;
  },

  async delete(payload: DeletePropertyApiRequest): Promise<ApiResponse<unknown>> {
    const { data } = await apiClient.delete<Promise<ApiResponse<unknown>>>(
      API_ENDPOINTS.PROPERTY.DELETE,
      {
        data: payload,
      }
    );
    return data;
  },

  async setMailingAddressOnProfile(mailingAddress: string): Promise<ApiResponse<unknown>> {
    const { data } = await apiClient.put<ApiResponse<unknown>>(API_ENDPOINTS.USER.PROFILE, {
      mailing_address: mailingAddress,
    });
    return data;
  },

  async getPropertyProtestStatus(
    propertyId: string
  ): Promise<ApiResponse<PropertyProtestStatusResponse>> {
    const { data } = await apiClient.get<ApiResponse<PropertyProtestStatusResponse>>(
      API_ENDPOINTS.PROPERTY.PROTEST_STATUS(propertyId)
    );
    return data;
  },

  async getProtestApplicability(propertyId: string): Promise<PropertyProtestApplicabilityResponse> {
    const { data } = await apiClient.get<ApiResponse<PropertyProtestApplicabilityResponse>>(
      API_ENDPOINTS.PROPERTY.PROTEST_APPLICABILITY(propertyId)
    );
    return data.data;
  },

  async createServiceExpansionRequest(
    payload: ServiceExpansionRequestPayload
  ): Promise<ServiceExpansionRequestResponse> {
    const { data } = await apiClient.post<ApiResponse<ServiceExpansionRequestResponse>>(
      API_ENDPOINTS.PROPERTY.SERVICE_EXPANSION_REQUEST,
      payload
    );
    return data.data;
  },
};

export const propertySupportService = {
  async getRealStateAutoCompletePropertyDetails(
    payload: PropertyRealStateAutoCompleteApiPayload
  ): Promise<PropertyRealStateAutoCompleteItemInfo[]> {
    const { data } = await apiClient.post<ApiResponse<PropertyRealStateAutoCompleteApiResponse>>(
      API_ENDPOINTS.PROPERTY.REAL_ESTATE_AUTOCOMPLETE,
      payload
    );

    console.log("propertyServices - Full API response:", data);
    console.log("propertyServices - data.success:", data.success);
    console.log("propertyServices - data.message:", data?.message);
    console.log("propertyServices - data.data:", data?.data);

    // Check if we have data even if success is false (some APIs return data with success=false)
    const responseData = data?.data;
    const hasData =
      responseData?.data && Array.isArray(responseData.data) && responseData.data.length > 0;
    const hasSuccessMessage = data?.message?.toLowerCase().includes("success");
    const statusCode = responseData?.statusCode;

    console.log("propertyServices - hasData:", hasData);
    console.log("propertyServices - hasSuccessMessage:", hasSuccessMessage);
    console.log("propertyServices - statusCode:", statusCode);

    // If we have data or a success message, return the data
    if (hasData || hasSuccessMessage || statusCode === 200 || data.success) {
      const results = responseData?.data || [];
      console.log("propertyServices - Returning results:", results);
      return results;
    }

    // If no data but success is true, return empty array
    if (data.success && (!responseData?.data || !Array.isArray(responseData.data))) {
      console.log("propertyServices - Success but no data, returning empty array");
      return [];
    }

    // Otherwise throw error
    console.error("propertyServices - Throwing error, no data found");
    throw new Error(data?.message || "Request failed");
  },

  async getPropertyDetailsFromAddress(
    payload: GetPropertyDetailsFromAddressApiPayload
  ): Promise<PropertyDetailsFromAddress[]> {
    const { data } = await apiClient.post<ApiResponse<PropertyDetailsFromAddressApiResponse>>(
      API_ENDPOINTS.PROPERTY.REAL_ESTATE_PROPERTY_SEARCH,
      payload
    );
    if (data.success) {
      return data?.data?.data;
    } else {
      throw new Error(data?.message || "Request failed");
    }
  },

  async getRealStatePropertyDetails(
    payload: PropertyRealStateDetailApiPayload
  ): Promise<PropertyRealStateDetailInfo> {
    const { data } = await apiClient.post<ApiResponse<PropertyRealStateDetailApiResponse>>(
      API_ENDPOINTS.PROPERTY.REAL_ESTATE_PROPERTY_DETAIL,
      payload
    );

    // Check if we have data even if success is false (some APIs return data with success=false)
    const responseData = data?.data?.data;
    const hasData = responseData !== null && responseData !== undefined;
    const hasSuccessMessage = data?.message?.toLowerCase().includes("success");
    const statusCode = data?.data?.statusCode;

    // If we have data or a success message, return the data
    if (hasData || hasSuccessMessage || statusCode === 200 || data.success) {
      return responseData;
    }

    // Otherwise throw error
    throw new Error(data?.message || "Request failed");
  },

  async getSexOffendersByLocation(
    payload: Partial<SexOffendersOrCrimeIncidentsPayload>
  ): Promise<SexOffendersResponse> {
    const { data } = await apiClient.get<ApiResponse<SexOffendersResponse>>(
      API_ENDPOINTS.PROPERTY.CRIME_SEX_OFFENDERS_LOCATION,
      { params: payload }
    );

    // Check if we have data even if success is false (some APIs return data with success=false)
    const responseData = data?.data;
    const hasData = responseData !== null && responseData !== undefined;
    const hasSuccessMessage = data?.message?.toLowerCase().includes("success");

    // If we have data or a success message, return the data
    if (hasData || hasSuccessMessage || data.success) {
      return responseData;
    }

    throw new Error(data?.message || "Failed to fetch sex offenders by location");
  },

  async getSexOffendersByZip(
    payload: Partial<SexOffendersOrCrimeIncidentsPayload>
  ): Promise<SexOffendersResponse> {
    const { data } = await apiClient.get<ApiResponse<SexOffendersResponse>>(
      API_ENDPOINTS.PROPERTY.CRIME_SEX_OFFENDERS_RECORDS,
      { params: payload }
    );

    // Check if we have data even if success is false (some APIs return data with success=false)
    const responseData = data?.data;
    const hasData = responseData !== null && responseData !== undefined;
    const hasSuccessMessage = data?.message?.toLowerCase().includes("success");

    // If we have data or a success message, return the data
    if (hasData || hasSuccessMessage || data.success) {
      return responseData;
    }

    throw new Error(data?.message || "Failed to fetch sex offenders by zip");
  },

  async getCrimeIncidents(
    payload: Partial<SexOffendersOrCrimeIncidentsPayload>
  ): Promise<CrimeIncidentsResponse> {
    const { data } = await apiClient.get<ApiResponse<CrimeIncidentsResponse>>(
      API_ENDPOINTS.PROPERTY.CRIME_INCIDENTS_LOCATION,
      { params: payload }
    );

    // Check if we have data even if success is false (some APIs return data with success=false)
    const responseData = data?.data;
    const hasData = responseData !== null && responseData !== undefined;
    const hasSuccessMessage = data?.message?.toLowerCase().includes("success");

    // If we have data or a success message, return the data
    if (hasData || hasSuccessMessage || data.success) {
      return responseData;
    }

    throw new Error(data?.message || "Failed to fetch crime incidents");
  },

  async getCrimeIncidentsByLocation(
    payload: Partial<SexOffendersOrCrimeIncidentsPayload>
  ): Promise<CrimeIncidentsResponse> {
    const { data } = await apiClient.get<ApiResponse<CrimeIncidentsResponse>>(
      API_ENDPOINTS.PROPERTY.CRIME_INCIDENTS_BY_LOCATION,
      { params: payload }
    );

    // Check if we have data even if success is false (some APIs return data with success=false)
    const responseData = data?.data;
    const hasData = responseData !== null && responseData !== undefined;
    const hasSuccessMessage = data?.message?.toLowerCase().includes("success");

    // If we have data or a success message, return the data
    if (hasData || hasSuccessMessage || data.success) {
      return responseData;
    }

    throw new Error(data?.message || "Failed to fetch crime incidents by location");
  },
};
